/*
 * Purpose: Assemble OutcomePlan → Workflow graph with START/GOAL anchors + layout
 * Last modified: 2026-07-10 | Agent
 * Completeness: 94
 */
import type { Edge, Node, Workflow } from '@/stores/workflowStore';
import { ensureMissionAnchors as ensureMissionAnchorsShared } from '@/lib/missionAnchors';
import { applyGoalMetadataToNodeConfig } from '@/lib/goalDrivenPlanning';
import { createJourneyTubeNode } from '@/lib/journeyTube';
import { DEFAULT_JOB_GMAIL_QUERY } from '@/services/connectors/gmailSource';
import type { OutcomePlan } from '@/services/autoblocks/types';

const genId = () => Math.random().toString(36).slice(2, 11);

const X_START = 40;
const Y_START = 120;
const X_GAP = 220;

function cloneTemplateWorkflow(templateKey: keyof typeof AVERAGE_USER_TEMPLATES, plan: OutcomePlan): Workflow {
  const tpl = AVERAGE_USER_TEMPLATES[templateKey];
  const now = new Date();
  const idMap = new Map<string, string>();

  const nodes: Node[] = tpl.nodes.map((node) => {
    const newId = genId();
    idMap.set(node.id, newId);
    return {
      ...node,
      id: newId,
      config: {
        ...node.config,
        _autoBlocksStepId: node.id,
        _autoBlocksFromTemplate: templateKey,
      },
    };
  });

  const edges: Edge[] = tpl.edges.map((edge) => ({
    id: genId(),
    from: idMap.get(edge.from) ?? edge.from,
    to: idMap.get(edge.to) ?? edge.to,
  }));

  return {
    id: genId(),
    name: `AutoBlocks: ${tpl.name}`,
    nodes,
    edges,
    createdAt: now,
    updatedAt: now,
    missionMeta: {
      allowDynamicMap: true,
      mapLocked: false,
      flowOrientation: 'auto',
      flowTracingMode: false,
      smartAutoConfigureOnConnect: true,
      smartAutoFixPolicy: 'safe-and-review',
      smartInputModalBehavior: 'badge-only',
      truthFactorStrictness: 'balanced',
    },
  };
}
function applyGmailInboundToWorkflow(workflow: Workflow, plan: OutcomePlan): Workflow {
  const wantsGmail =
    Boolean(plan.gmailInboxId) ||
    plan.useGmailInbound ||
    plan.steps.some((s) => s.subtype === 'gmail_inbound');
  if (!wantsGmail) return workflow;

  const triggerStep = plan.steps.find(
    (s) => s.type === 'trigger' && (s.subtype === 'gmail_inbound' || s.subtype === 'webhook')
  );
  const plannedSource = triggerStep?.config?.source as string | undefined;
  const plannedQuery = triggerStep?.config?.gmailQuery as string | undefined;

  const nodes = workflow.nodes.map((node) => {
    if (node.type !== 'trigger') return node;
    if (node.subtype !== 'webhook' && node.subtype !== 'gmail_inbound') return node;

    const source =
      plannedSource ||
      (plan.gmailInboxId ? 'webhook' : 'gmail_api');

    return {
      ...node,
      subtype: 'gmail_inbound' as const,
      config: {
        ...node.config,
        name: node.config.name || 'Gmail email received',
        inboxId: source === 'webhook' ? plan.gmailInboxId || node.config.inboxId : undefined,
        postUrl: source === 'webhook' ? plan.gmailPostUrl || node.config.postUrl : undefined,
        source,
        gmailQuery: plannedQuery || node.config.gmailQuery || DEFAULT_JOB_GMAIL_QUERY,
      },
    };
  });

  return { ...workflow, nodes };
}

/** Preserve positions and stable ids when refining an existing map. */
export function mergeWorkflowRefinement(
  prior: Workflow | null | undefined,
  assembled: Workflow
): Workflow {
  if (!prior) return assembled;

  const priorByStepId = new Map<string, Node>();
  const priorByLabel = new Map<string, Node>();

  for (const node of prior.nodes) {
    const stepId = node.config._autoBlocksStepId as string | undefined;
    if (stepId) priorByStepId.set(stepId, node);
    const label = String(node.config.name || node.config.label || '').trim().toLowerCase();
    if (label) priorByLabel.set(label, node);
  }

  const mergedNodes = assembled.nodes.map((node) => {
    const stepId = node.config._autoBlocksStepId as string | undefined;
    const label = String(node.config.name || node.config.label || '').trim().toLowerCase();
    const priorNode =
      (stepId ? priorByStepId.get(stepId) : undefined) ??
      (label ? priorByLabel.get(label) : undefined);

    if (!priorNode) return node;

    return {
      ...node,
      id: priorNode.id,
      x: priorNode.x,
      y: priorNode.y,
      config: {
        ...priorNode.config,
        ...node.config,
        _autoBlocksStepId: stepId ?? priorNode.config._autoBlocksStepId,
      },
    };
  });

  for (const anchorType of ['start', 'goal', 'tube'] as const) {
    const priorAnchor = prior.nodes.find((n) => n.type === anchorType);
    const mergedAnchor = mergedNodes.find((n) => n.type === anchorType);
    if (priorAnchor && mergedAnchor) {
      mergedAnchor.id = priorAnchor.id;
      mergedAnchor.x = priorAnchor.x;
      mergedAnchor.y = priorAnchor.y;
      mergedAnchor.config = { ...priorAnchor.config, ...mergedAnchor.config };
    }
  }

  const idMap = new Map<string, string>();
  for (let i = 0; i < assembled.nodes.length; i++) {
    idMap.set(assembled.nodes[i].id, mergedNodes[i]?.id ?? assembled.nodes[i].id);
  }

  const mergedEdges = assembled.edges.map((edge) => ({
    ...edge,
    id: genId(),
    from: idMap.get(edge.from) ?? edge.from,
    to: idMap.get(edge.to) ?? edge.to,
  }));

  return {
    ...assembled,
    id: prior.id,
    name: assembled.name || prior.name,
    createdAt: prior.createdAt,
    missionMeta: { ...prior.missionMeta, ...assembled.missionMeta },
    nodes: mergedNodes,
    edges: mergedEdges,
  };
}

export function assembleWorkflowFromPlan(plan: OutcomePlan, name?: string): Workflow {
  const now = new Date();
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const nodeIdByStep = new Map<string, string>();

  plan.steps.forEach((step, index) => {
    const nodeId = genId();
    nodeIdByStep.set(step.id, nodeId);
    nodes.push({
      id: nodeId,
      type: step.type as Node['type'],
      subtype: step.subtype as Node['subtype'],
      config: applyGoalMetadataToNodeConfig(step, { ...step.config }),
      x: X_START + index * X_GAP,
      y: Y_START,
    });
  });
  for (let i = 0; i < plan.steps.length - 1; i++) {
    const from = nodeIdByStep.get(plan.steps[i].id);
    const to = nodeIdByStep.get(plan.steps[i + 1].id);
    if (from && to) {
      edges.push({ id: genId(), from, to });
    }
  }

  let workflow: Workflow = {
    id: genId(),
    name: name ?? `AutoBlocks: ${truncate(plan.interpretedGoal, 48)}`,
    nodes,
    edges,
    createdAt: now,
    updatedAt: now,
    missionMeta: {
      allowDynamicMap: true,
      mapLocked: false,
      flowOrientation: 'auto',
      flowTracingMode: false,
      smartAutoConfigureOnConnect: true,
      smartAutoFixPolicy: 'safe-and-review',
      smartInputModalBehavior: 'badge-only',
      truthFactorStrictness: 'balanced',
    },
  };

  if (name) workflow = { ...workflow, name };

  return applyGmailInboundToWorkflow(
    applyJourneyTubeIfNeeded(ensureMissionAnchorsForPlan(workflow, plan), plan),
    plan
  );
}

function ensureMissionAnchorsForPlan(workflow: Workflow, plan: OutcomePlan): Workflow {
  const outcome = (plan.interpretedGoal || plan.rawOutcome || '').trim();
  return ensureMissionAnchorsShared(workflow, { outcome: outcome || undefined, minimalRunnable: true });
}

function applyJourneyTubeIfNeeded(workflow: Workflow, plan: OutcomePlan): Workflow {
  if (plan.director?.pacing !== 'journey_tube') {
    return workflow;
  }

  const start = workflow.nodes.find((n) => n.type === 'start');
  const goal = workflow.nodes.find((n) => n.type === 'goal');
  if (!start || !goal) return workflow;

  const phases = plan.director.journeyPhases ?? ['Let the outcome unfold', 'Attach blocks when ready'];
  const tube = createJourneyTubeNode(plan.id, phases, X_START + X_GAP, Y_START);

  const nodes = [
    ...workflow.nodes.filter((n) => n.type !== 'tube'),
    { ...tube, config: { ...tube.config, _autoBlocksDirector: plan.director } },
  ];

  const withoutDirectStartGoal = workflow.edges.filter(
    (e) => !(e.from === start.id && e.to === goal.id)
  );

  const edges = [
    ...withoutDirectStartGoal.filter((e) => e.from !== start.id || e.to !== goal.id),
    { id: genId(), from: start.id, to: tube.id, kind: 'journey_tube' as const },
    { id: genId(), from: tube.id, to: goal.id, kind: 'journey_tube' as const },
  ];

  const operational = workflow.nodes.filter(
    (n) => n.type === 'trigger' || n.type === 'tool' || n.type === 'action'
  );
  if (operational[0]) {
    edges.push({ id: genId(), from: tube.id, to: operational[0].id, kind: 'link' });
  }

  return { ...workflow, nodes, edges };
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

export function attachWorkflowPreview(plan: OutcomePlan): OutcomePlan {
  const workflow = assembleWorkflowFromPlan(plan);
  return {
    ...plan,
    workflowPreview: {
      name: workflow.name,
      nodes: workflow.nodes,
      edges: workflow.edges,
    },
  };
}
